import { useEffect, useRef, useState, useCallback } from 'react';

export interface Transform {
  scale: number;
  tx: number;
  ty: number;
}

const IDENTITY: Transform = { scale: 1, tx: 0, ty: 0 };

interface Options {
  minScale?: number;
  maxScale?: number;
  /** Called when the user double-taps. */
  onDoubleTap?: () => void;
}

/**
 * Pinch-zoom + 1-finger pan via Pointer Events.
 *
 * Designed to wrap a container element. `onPointerDown/Move/Up/Cancel` are
 * forwarded to the JSX. The hook intentionally does NOT consume single-pointer
 * downs unless they later become a 2-pointer gesture — that lets the parent
 * still use single taps for point dragging on the annotation layer.
 *
 * To enable PAN with one finger, pass `panSinglePointer: true` (used on the
 * preview/freeze view; OFF on the annotation layer where one finger drags points).
 */
export function usePinchPan(options: Options & { panSinglePointer?: boolean } = {}) {
  const { minScale = 1, maxScale = 6, onDoubleTap, panSinglePointer = false } = options;
  const [transform, setTransform] = useState<Transform>(IDENTITY);
  const transformRef = useRef(transform);
  transformRef.current = transform;

  const pointers = useRef<Map<number, { x: number; y: number }>>(new Map());
  const gestureStart = useRef<{
    dist: number;
    mid: { x: number; y: number };
    transform: Transform;
  } | null>(null);
  const lastTap = useRef(0);

  const reset = useCallback(() => setTransform(IDENTITY), []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      (e.target as Element).setPointerCapture?.(e.pointerId);
      pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

      // Double-tap detection
      const now = Date.now();
      if (now - lastTap.current < 300 && pointers.current.size === 1) {
        onDoubleTap?.();
      }
      lastTap.current = now;

      if (pointers.current.size === 2) {
        const [p1, p2] = Array.from(pointers.current.values());
        gestureStart.current = {
          dist: Math.hypot(p1.x - p2.x, p1.y - p2.y),
          mid: { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 },
          transform: transformRef.current,
        };
      }
    },
    [onDoubleTap],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!pointers.current.has(e.pointerId)) return;
      const prev = pointers.current.get(e.pointerId)!;
      pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

      // 2-finger pinch
      if (pointers.current.size >= 2 && gestureStart.current) {
        const [p1, p2] = Array.from(pointers.current.values());
        const newDist = Math.hypot(p1.x - p2.x, p1.y - p2.y);
        const newMid = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
        const ratio = newDist / gestureStart.current.dist;
        const t0 = gestureStart.current.transform;
        const newScale = Math.max(
          minScale,
          Math.min(maxScale, t0.scale * ratio),
        );

        // Pan offset = how far the midpoint moved + scale-around-midpoint correction
        const dx = newMid.x - gestureStart.current.mid.x;
        const dy = newMid.y - gestureStart.current.mid.y;
        const scaleDelta = newScale / t0.scale;
        const tx =
          (t0.tx - gestureStart.current.mid.x) * scaleDelta +
          gestureStart.current.mid.x +
          dx;
        const ty =
          (t0.ty - gestureStart.current.mid.y) * scaleDelta +
          gestureStart.current.mid.y +
          dy;
        setTransform({ scale: newScale, tx, ty });
        return;
      }

      // 1-finger pan (only if enabled)
      if (panSinglePointer && pointers.current.size === 1) {
        const dx = e.clientX - prev.x;
        const dy = e.clientY - prev.y;
        setTransform((t) => ({ ...t, tx: t.tx + dx, ty: t.ty + dy }));
      }
    },
    [maxScale, minScale, panSinglePointer],
  );

  const onPointerUp = useCallback((e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) {
      gestureStart.current = null;
    }
  }, []);

  // Disable native pinch-zoom on the page while gesture is active.
  useEffect(() => {
    const prev = document.body.style.touchAction;
    document.body.style.touchAction = 'none';
    return () => {
      document.body.style.touchAction = prev;
    };
  }, []);

  return {
    transform,
    reset,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerUp,
    },
  };
}
